import { getProgrammePortalData } from '../data/programmePortalData';
import { apiCall } from './api';

export const PROGRAMME_FRONTEND_MODE = 'connected';

const STORAGE_PREFIX = 'supernova-programme-preview:v2';

const storageKey = (kind, recordType) =>
  `${STORAGE_PREFIX}:${kind}:${recordType}`;

const readStorage = (key, fallback) => {
  if (typeof window === 'undefined') return fallback;

  try {
    const stored = window.localStorage.getItem(key);
    return stored ? JSON.parse(stored) : fallback;
  } catch (error) {
    return fallback;
  }
};

const writeStorage = (key, value) => {
  if (typeof window === 'undefined') return;

  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    // The preview remains usable when browser storage is unavailable.
  }
};

const getInitials = (fullName) =>
  String(fullName || 'Maya Clarke')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('');

const buildDefaultTasks = (kind) =>
  getProgrammePortalData(kind).onboardingTasks.map((task) => ({
    ...task,
    status: task.status || (task.complete ? 'complete' : 'not_started'),
    values: Object.fromEntries(
      task.fields.map((field) => [field.name, field.defaultValue ?? ''])
    ),
  }));

export const saveApplicationDraft = (kind, data) => {
  writeStorage(storageKey(kind, 'draft'), {
    data,
    savedAt: new Date().toISOString(),
  });
};

export const getApplicationDraft = (kind) =>
  readStorage(storageKey(kind, 'draft'), null);

export const clearApplicationDraft = (kind) => {
  if (typeof window === 'undefined') return;
  window.localStorage.removeItem(storageKey(kind, 'draft'));
};

export const submitProgrammeApplication = async (kind, data) => {
  const application = await apiCall('/programme-applications', {
    method: 'POST',
    body: JSON.stringify({ kind, data }),
  });

  writeStorage(storageKey(kind, 'application'), application);
  clearApplicationDraft(kind);

  return application;
};

export const getProgrammeApplication = (kind) =>
  readStorage(storageKey(kind, 'application'), null);

export const createProgrammeSession = async (kind, email, password) => {
  const session = await apiCall('/programme-auth/session', {
    method: 'POST',
    body: JSON.stringify({ kind, email, password }),
  });
  writeStorage(storageKey(kind, 'session'), session);
  return session;
};

export const getProgrammeSession = (kind) =>
  readStorage(storageKey(kind, 'session'), null);

export const clearProgrammeSession = (kind) => {
  if (typeof window === 'undefined') return;
  window.localStorage.removeItem(storageKey(kind, 'session'));
};

export const requestProgrammeAccess = async (
  kind,
  email,
  requestType,
  details = {}
) => {
  const endpoint =
    requestType === 'password-reset'
      ? '/programme-auth/password-reset'
      : '/programme-auth/access';
  let body;
  if (requestType === 'setup') {
    body = new FormData();
    body.append('kind', kind);
    body.append('email', email);
    body.append('reference', details.reference || '');
    body.append('password', details.password || '');
    if (details.profileImage) {
      body.append('profile_image', details.profileImage);
    }
  } else {
    body = JSON.stringify({ kind, email, ...details });
  }

  const request = await apiCall(endpoint, { method: 'POST', body });
  writeStorage(storageKey(kind, `access-${requestType}`), request);
  return request;
};

export const completeProgrammePasswordReset = async (kind, token, password) =>
  apiCall('/programme-auth/password-reset/complete', {
    method: 'POST',
    body: JSON.stringify({ kind, token, password }),
  });

const portalRequest = (kind, endpoint = '', options = {}) => {
  const token = getProgrammeSession(kind)?.token;
  return apiCall(`/programme-portals/${kind}${endpoint}`, {
    ...options,
    headers: {
      ...options.headers,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
};

export const getLiveProgrammePortal = (kind, options) =>
  portalRequest(kind, '', options);

export const getLiveProgrammeUpdates = (kind, options) =>
  portalRequest(kind, '/updates', options);

export const getLiveProgrammeResources = (kind, options) =>
  portalRequest(kind, '/resources', options);

export const getLiveProgrammeCredential = (options) =>
  portalRequest('zone', '/credential', options);

export const getLiveProgrammeCohort = (options) =>
  portalRequest('fellowship', '/cohort', options);

export const updateLiveOnboardingTask = (kind, taskId, values, complete = true) =>
  portalRequest(kind, `/onboarding/${encodeURIComponent(taskId)}`, {
    method: 'PUT',
    body: JSON.stringify({ values, complete }),
  });

export const getPortalProfile = (kind) => {
  const portalData = getProgrammePortalData(kind);
  const application = getProgrammeApplication(kind);
  const session = getProgrammeSession(kind);
  const access = readStorage(storageKey(kind, 'access-setup'), null);
  const sessionMatchesApplication =
    application?.data?.email &&
    session?.email &&
    application.data.email.toLowerCase() === session.email.toLowerCase();

  if (!application || !sessionMatchesApplication) {
    return {
      ...portalData.defaultProfile,
      email: session?.email || portalData.defaultProfile.email,
      profileImage: access?.profileImage || null,
      isExample: true,
    };
  }

  const fullName =
    application.data.fullName || portalData.defaultProfile.fullName;

  return {
    fullName,
    email: application.data.email || session?.email,
    initials: getInitials(fullName),
    profileImage: access?.profileImage || null,
    applicationReference: application.id,
    applicationStatus: application.status,
    isExample: false,
  };
};

export const getOnboardingTasks = (kind) => {
  const savedTasks = readStorage(storageKey(kind, 'onboarding'), null);
  return savedTasks || buildDefaultTasks(kind);
};

export const saveOnboardingTask = (kind, taskId, values, complete = true) => {
  const tasks = getOnboardingTasks(kind).map((task) =>
    task.id === taskId
      ? {
          ...task,
          complete,
          status: complete ? 'complete' : 'in_progress',
          values,
        }
      : task
  );

  writeStorage(storageKey(kind, 'onboarding'), tasks);
  return tasks;
};

export const PROGRAMME_BACKEND_OPERATIONS = Object.freeze({
  submitApplication: 'POST /programme-applications',
  getApplication: 'GET /programme-applications/:applicationId',
  createSession: 'POST /programme-auth/session',
  requestAccess: 'POST /programme-auth/access',
  requestPasswordReset: 'POST /programme-auth/password-reset',
  completePasswordReset: 'POST /programme-auth/password-reset/complete',
  getPortal: 'GET /programme-portals/:programme',
  updateOnboardingTask:
    'PUT /programme-portals/:programme/onboarding/:taskId',
  getUpdates: 'GET /programme-portals/:programme/updates',
  getResources: 'GET /programme-portals/:programme/resources',
  getCredential: 'GET /programme-portals/zone/credential',
  getCohort: 'GET /programme-portals/fellowship/cohort',
});
